import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

const WHATSAPP_HREF = "https://wa.me/919326800270";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#2E2E31]/90 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-[#111111]/40 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Logo variant="header" />

        <nav className="hidden xl:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-outfit text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-[#D4AF37]" : "text-white hover:text-[#D4AF37]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4 shrink-0">
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noreferrer"
            aria-label="Chat on WhatsApp"
            data-testid="nav-whatsapp"
            className="hidden md:flex w-10 h-10 items-center justify-center border border-white/20 hover:border-[#25D366] hover:text-[#25D366] text-white transition-colors duration-300"
          >
            <MessageCircle size={16} />
          </a>

          <button
            onClick={() => setOpen(!open)}
            className="xl:hidden text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-[#111111]/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-outfit text-sm uppercase tracking-[0.22em] ${
                    isActive ? "text-[#D4AF37]" : "text-white"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="hairline my-2" />
            <a href="tel:+919326800270" className="font-outfit text-sm text-[#D9D9D9]" data-testid="mobile-phone">
              +91 93268 00270
            </a>
            <a href="mailto:kirti@ikoaedge.com" className="font-outfit text-sm text-[#D9D9D9]" data-testid="mobile-email">
              kirti@ikoaedge.com
            </a>
            <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp" className="inline-flex items-center gap-2 font-outfit text-sm text-[#25D366] mt-1">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
